<script setup lang="ts">
import {
    type TextAlignTypes,
    type TypographyTypes,
    type FontSizes,
    type FontWeights,
    type WhiteSpaceTypes,
    type SizeConditional,
    TypographyAlignmentsDefault,
    useExtractSize,
    type PaletteColorProp,
} from '#imports';

const {
    type = 'p',
    size = 'medium',
    weight = '400',
    color = 'inherit',
    align = TypographyAlignmentsDefault,
    breakpoint = '1000px',
    noReset,
    grow,
    whiteSpace,
} = defineProps<{
    type?: TypographyTypes;
    size?: SizeConditional<FontSizes>;
    weight?: SizeConditional<FontWeights>;
    align?: SizeConditional<TextAlignTypes>;
    color?: PaletteColorProp | string;
    whiteSpace?: SizeConditional<WhiteSpaceTypes>;
    noReset?: boolean;
    grow?: boolean;
    breakpoint?: string | false;
}>();

const { mobile: mTextAlign, desktop: dTextAlign } = useExtractSize(
    () => align,
    TypographyAlignmentsDefault
);

const { mobile: mWhiteSpace, desktop: dWhiteSpace } = useExtractSize(
    () => whiteSpace
);

const { mobile: mWeight, desktop: dWeight } = useExtractSize(
    () => weight,
    '400'
);

const { mobile: mSize, desktop: dSize } = useExtractSize(() => size, 'medium');
</script>

<template>
    <component
        :is="type"
        class="font-configs"
        :class="[
            dSize,
            `m-${mSize}`,
            { reset: !noReset, inherit: noReset, grow },
        ]"
    >
        <slot />
    </component>
</template>

<style scoped lang="scss">
$weight: v-bind(dWeight);
$mobile-weight: v-bind(mWeight);
$breakpoint: v-bind(breakpoint);

.reset {
    margin: 0;
    padding: 0;
}
.grow {
    flex-grow: 1;
}
.inherit {
    font-size: inherit !important;
    font-weight: inherit !important;
    text-align: inherit !important;
}

.font-configs {
    text-align: v-bind(dTextAlign) !important;
    white-space: v-bind(dWhiteSpace) !important;
    color: v-bind(color) !important;
    @include mobile-only {
        white-space: v-bind(mWhiteSpace) !important;
        text-align: v-bind(mTextAlign) !important;
    }
}

.link {
    @include desktop-only {
        @include tg-link();
    }
}
.x-small {
    @include desktop-only {
        @include tg-xs($weight);
    }
}
.small {
    @include desktop-only {
        @include tg-s($weight);
    }
}
.medium {
    @include desktop-only {
        @include tg-m($weight);
    }
}
.large {
    @include desktop-only {
        @include tg-l($weight);
    }
}
.x-large {
    @include desktop-only {
        @include tg-xl($weight);
    }
}
.display1 {
    @include desktop-only {
        @include tg-display1($weight);
    }
}

.display2 {
    @include desktop-only {
        @include tg-display2($weight);
    }
}

.display3 {
    @include desktop-only {
        @include tg-display3($weight);
    }
}

.display4 {
    @include desktop-only {
        @include tg-display4($weight);
    }
}

.m-link {
    @include mobile-only() {
        @include tg-link($mobile-weight);
    }
}
.m-x-small {
    @include mobile-only() {
        @include tg-xs($mobile-weight);
    }
}
.m-small {
    @include mobile-only() {
        @include tg-s($mobile-weight);
    }
}
.m-medium {
    @include mobile-only() {
        @include tg-m($mobile-weight);
    }
}
.m-large {
    @include mobile-only() {
        @include tg-l($mobile-weight);
    }
}
.m-x-large {
    @include mobile-only() {
        @include tg-xl($mobile-weight);
    }
}
.m-display1 {
    @include mobile-only() {
        @include tg-display1($mobile-weight);
    }
}
.m-display2 {
    @include mobile-only() {
        @include tg-display2($mobile-weight);
    }
}
.m-display3 {
    @include mobile-only() {
        @include tg-display3($mobile-weight);
    }
}
.m-display4 {
    @include mobile-only() {
        @include tg-display4($mobile-weight);
    }
}
</style>
